import type { Process, ProcessProps } from "@certa/studio-core";
import { dbOps } from "../hooks/dbOps.store";
import { importRulesToStudio } from "./rule.actions";
import { Intents } from "@certa/studio-core/src/constants/activityLogs.constants";
import { useImportProcessJSON } from "../../useImportProcessJSON";
import type { Process as RawProcess } from "../../../types/rawProcess.types";
import type { EventRaw } from "@certa/queries/schemas/studio/eca.schema";
import type {
  ZodRawProcess,
  ZodTemplateProcessProps,
  ZodOnCreateWorkflow
} from "@certa/queries/schemas/studio/deployableJSON.schema";
import { getMergedDefaultStepsForGroups } from "./utils/processSchemaMerge";
import { mergeStringArrayMaps } from "@certa/common/utils/object";
import { getUniqueArray } from "@certa/common/utils/array";
import { ToastTypes, showToast } from "@certa/catalyst/components/Toast";
import {
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { Link } from "@certa/catalyst/components/Link";
import { getStudioAppDetailsRoute } from "@certa/common/utils/routes";
import { createStateRule, createEventRule } from "../../../models/eca.model";
import type {
  EventRule,
  StateRule
} from "@certa/studio-core/src/types/eca.types";
import type { AppSettings } from "@certa/queries/types/appSettings.types";
import { reconcileReferencedAppSettings } from "../../../utils/reconcileReferencedAppSettings";
import { getEnvironment } from "../../../utils/appSettings.providers";

export const importProcess = (
  processId: string,
  metaJSON: Process,
  config: {
    shouldSkipUpdatingModifiedAtTimestamp?: boolean;
    shouldSkipClearingPreviousLedgerAndUndoRedoStack?: boolean;
    shouldLogChangesInLedgerAndUndoStack?: boolean;
  } = {}
) => {
  const {
    shouldSkipUpdatingModifiedAtTimestamp = false,
    shouldSkipClearingPreviousLedgerAndUndoRedoStack = false,
    shouldLogChangesInLedgerAndUndoStack = false
  } = config;
  dbOps().transaction(
    () => {
      dbOps().setProcess(
        processId,
        metaJSON.process,
        shouldSkipUpdatingModifiedAtTimestamp
      );
      dbOps().bulkSetBulkActions(
        processId,
        Object.values(metaJSON.bulkActions),
        shouldSkipUpdatingModifiedAtTimestamp
      );
      dbOps().bulkSetSwimlanes(
        processId,
        Object.values(metaJSON.swimlanes),
        shouldSkipUpdatingModifiedAtTimestamp
      );
      dbOps().bulkSetSteps(
        processId,
        Object.values(metaJSON.steps),
        shouldSkipUpdatingModifiedAtTimestamp
      );
      dbOps().bulkSetFields(
        processId,
        Object.values(metaJSON.fields),
        shouldSkipUpdatingModifiedAtTimestamp
      );
      dbOps().replaceAllEventRules(
        processId,
        metaJSON.eventRules || {},
        shouldSkipUpdatingModifiedAtTimestamp
      );
      dbOps().replaceAllStateRules(
        processId,
        metaJSON.stateRules || {},
        shouldSkipUpdatingModifiedAtTimestamp
      );
      importRulesToStudio({
        processId,
        ruleMap: metaJSON.rules,
        conditionMap: metaJSON.conditions,
        conditionGroupMap: metaJSON.conditionGroups,
        shouldSkipUpdatingModifiedAtTimestamp
      });

      // Clears Ledger and Undo/Redo Stack entries
      // before the import of the process
      // (does not clear changes generated by the import of the process itself)
      if (!shouldSkipClearingPreviousLedgerAndUndoRedoStack) {
        const processId = metaJSON.process.id;
        dbOps().clearChangesForApp();
        dbOps().clearUndoStack(processId);
        dbOps().clearRedoStack(processId);
      }
    },
    {
      skipLoggingInLedgerAndUndoStack: !shouldLogChangesInLedgerAndUndoStack,
      intent: Intents.IMPORT_JSON
    }
  );
};

export const replaceProcess = (
  processId: string,
  metaJSON: Process,
  config: {
    shouldSkipClearingPreviousLedgerAndUndoRedoStack?: boolean;
    shouldLogChangesInLedgerAndUndoStack?: boolean;
  } = {}
) => {
  const {
    shouldSkipClearingPreviousLedgerAndUndoRedoStack = false,
    shouldLogChangesInLedgerAndUndoStack = false
  } = config;
  dbOps().transaction(
    () => {
      dbOps().replaceProcess(processId, metaJSON);

      // Clears Ledger and Undo/Redo Stack entries
      // before the import of the process
      // (does not clear changes generated by the import of the process itself)
      if (!shouldSkipClearingPreviousLedgerAndUndoRedoStack) {
        const processId = metaJSON.process.id;
        dbOps().clearChangesForApp();
        dbOps().clearUndoStack(processId);
        dbOps().clearRedoStack(processId);
      }
    },
    {
      skipLoggingInLedgerAndUndoStack: !shouldLogChangesInLedgerAndUndoStack,
      intent: Intents.IMPORT_JSON
    }
  );
};

/**
 * Deletes components related to a process.
 *
 * No need to delete process, as the main purpose
 * of this function is to delete old related components of a process so that
 * we are not storing unreferenced components in db. Process itself
 * is eventually going to be imported again by importProcess function.
 */
export const deleteProcess = (
  processId: string,
  config: {
    shouldSkipClearingLedger?: boolean;
    shouldLogChangesInLedgerAndUndoStack?: boolean;
  } = {}
) => {
  const {
    shouldSkipClearingLedger = false,
    shouldLogChangesInLedgerAndUndoStack = false
  } = config;
  dbOps().transaction(
    () => {
      dbOps().deleteProcess(processId);
      if (!shouldSkipClearingLedger) {
        dbOps().clearChangesForApp();
      }
    },
    {
      skipLoggingInLedgerAndUndoStack: !shouldLogChangesInLedgerAndUndoStack
    }
  );
};

export const undo = () =>
  dbOps().transaction(() => dbOps().undo(), { intent: Intents.UNDO });
export const redo = () =>
  dbOps().transaction(() => dbOps().redo(), { intent: Intents.REDO });

/** Returns previous commit id from the top of undo stack else null if the undo stack is empty */
export const getPreviousCommitId = () => {
  const prevCommitId = dbOps().stateManager?.meta.getPreviousCommitId();
  return prevCommitId || null;
};

/**
 * @param commitId - The commitId to revert until, or null to revert all changes in the undo stack
 */
export const revertToParticularCommit = ({
  commitId
}: {
  commitId: string | null;
}) => {
  dbOps().transaction(
    () => {
      try {
        dbOps().revertToParticularCommit(commitId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    { intent: Intents.REVERT_TO_OLDER_COMMIT }
  );
};

export const updateProcess = (props: {
  processId: string;
  processProps: Partial<ProcessProps>;
  shouldSkipChange?: boolean;
  shouldSkipUpdatingModifiedAtTimestamp?: boolean;
}) =>
  dbOps().transaction(
    () => {
      dbOps().updateProcess(props.processId, props.processProps, {
        shouldSkipChange: props.shouldSkipChange,
        shouldSkipUpdatingModifiedAtTimestamp:
          props.shouldSkipUpdatingModifiedAtTimestamp
      });
    },
    { intent: Intents.UPDATE_PROCESS }
  );

// This is a hook because it needs to be used in the useImportProcessJSON hook
export const useImportMultiProcessTemplate = ({
  branchId,
  packageId,
  savepointId
}: {
  branchId: number;
  packageId: number;
  savepointId: number;
}) => {
  const { importJSON } = useImportProcessJSON({
    packageId,
    branchId,
    savepointId
  });

  const importMultiProcessTemplate = async ({
    processId,
    processesToImport,
    addToRelatedTypes
  }: {
    processId: string;
    processesToImport: RawProcess[] | ZodRawProcess[];
    addToRelatedTypes: string[];
  }) => {
    try {
      showToast({
        title: "Related Process Creation Started",
        description: "Adding related dependencies",
        type: ToastTypes.INFO
      });

      await Promise.all(processesToImport.map(p => importJSON(p)));

      const process = dbOps().getProcess(processId);

      if (process) {
        dbOps().transaction(
          () => {
            dbOps().updateProcess(processId, {
              relatedTypes: [
                ...(process?.relatedTypes ?? []),
                ...addToRelatedTypes
              ]
            });
          },
          { intent: Intents.IMPORT_MULTI_PROCESS_TEMPLATE }
        );
      }

      showToast({
        title: "Related Process Creation Finished!",
        description: (
          <Typography
            variant={TypographyVariants.LABEL_SM}
            color={TypographyColors.NEUTRAL_700}
          >
            Related dependencies added successfully.
            <Link
              variant={TypographyVariants.LABEL_SM}
              href={getStudioAppDetailsRoute({
                id: packageId,
                branch: branchId,
                savepoint: savepointId
              })}
              target="_blank"
              label="Open App details"
            />
          </Typography>
        ),
        type: ToastTypes.SUCCESS
      });
      return { process, error: null };
    } catch (error) {
      showToast({
        title: "Related Process Creation Failed",
        description: "Related dependencies failed to create",
        type: ToastTypes.ERROR
      });
      return { process: null, error: "Related Process Creation Failed" };
    }
  };

  return { importMultiProcessTemplate };
};

export const updateOnCreateWorkflow = (props: {
  processId: string;
  event: EventRaw;
  enabled: boolean;
}) => {
  const { event, enabled: isEnabled, processId } = props;

  if (!processId) {
    throw new Error("Process ID is not set");
  }

  let onCreateWorkflow: ZodOnCreateWorkflow[] = [
    ...(dbOps().getProcessDef(processId).process.onCreateWorkflow || [])
  ];

  if (event.name === "create_workflow") {
    throw new Error(
      "create_workflow event is not allowed in on_create_workflow"
    );
  }

  if (event.name === "archive_workflow") {
    throw new Error(
      "archive_workflow event is not allowed in on_create_workflow"
    );
  }

  if ("on_tag" in event) {
    if (isEnabled) {
      onCreateWorkflow.push({
        type: "event",
        name: event.name,
        tag: event.on_tag
      });
    } else {
      onCreateWorkflow = onCreateWorkflow.filter(
        config => event.name === config.name && config.tag !== event.on_tag
      );
    }
  } else {
    if (isEnabled) {
      onCreateWorkflow.push({
        type: "event",
        name: event.name
      });
    } else {
      onCreateWorkflow = onCreateWorkflow.filter(
        config => event.name !== config.name
      );
    }
  }
  dbOps().transaction(
    () => {
      dbOps().updateProcess(processId, {
        onCreateWorkflow
      });
    },
    { intent: Intents.UPDATE_ON_CREATE_WORKFLOW }
  );
};

export const updateProcessFromTemplate = (
  processId: string,
  processProps: ZodTemplateProcessProps
) => {
  const process = dbOps().getProcess(processId);

  if (process) {
    const modifiedProcess: Partial<ProcessProps> = {};

    modifiedProcess.relatedTypes = getUniqueArray(
      process?.relatedTypes,
      processProps.related_types
    );

    modifiedProcess.staticGroups = getUniqueArray(
      process?.staticGroups,
      processProps.static_groups
    );

    modifiedProcess.dynamicGroupNames = getUniqueArray(
      process?.dynamicGroupNames,
      processProps.dynamic_group_names
    );

    modifiedProcess.dynamicGroupNamesWithPerm = mergeStringArrayMaps(
      process.dynamicGroupNamesWithPerm,
      processProps.dynamic_group_names_with_perm
    );

    modifiedProcess.defaultStepTags = getUniqueArray(
      process.defaultStepTags,
      processProps.default_step_tags
    );

    modifiedProcess.defaultStepTagForGroups = getMergedDefaultStepsForGroups(
      process.defaultStepTagForGroups,
      processProps.default_steps_for_groups
    );
    if (processProps?.mentions_template) {
      modifiedProcess.mentionsTemplate = processProps.mentions_template;
    }

    modifiedProcess.autoIntegrationCallFieldTags = getUniqueArray(
      process.autoIntegrationCallFieldTags,
      processProps.auto_integration_call_field_tags
    );

    modifiedProcess.onDeleteIntegrationFieldTags = getUniqueArray(
      process.onDeleteIntegrationFieldTags,
      processProps.on_delete_integration_field_tags
    );

    if (processProps.kind) {
      modifiedProcess.kind = {
        ...process.kind,
        availableStatuses: getUniqueArray(
          process.kind.availableStatuses,
          processProps.kind?.available_statuses
        ),
        visibleToStaticGroups: getUniqueArray(
          process.kind.visibleToStaticGroups,
          processProps.kind?.visible_to_static_groups
        ),
        permissions: {
          dynamicGroups:
            mergeStringArrayMaps(
              process.kind.permissions.dynamicGroups,
              processProps.kind?.permissions.dynamic_groups
            ) || {},
          staticGroups:
            mergeStringArrayMaps(
              process.kind.permissions.staticGroups,
              processProps.kind?.permissions.static_groups
            ) || {}
        }
      };
    }
    let eventRules: Record<string, EventRule> | null = null;
    let stateRules: Record<string, StateRule> | null = null;

    // Only add eventRules if event_rules are present in rawProcess
    if (processProps.event_rules && processProps.event_rules.length > 0) {
      eventRules = processProps.event_rules.reduce(
        (acc: Record<string, EventRule>, eventRuleRaw) => {
          const eventRule = createEventRule(eventRuleRaw);
          acc[eventRule.__studioId] = eventRule;
          return acc;
        },
        {}
      );
    }
    if (processProps.states && processProps.states.length > 0) {
      stateRules = processProps.states.reduce(
        (acc: Record<string, StateRule>, statesRaw) => {
          const stateRule = createStateRule(statesRaw);
          acc[stateRule.__studioId] = stateRule;
          return acc;
        },
        {}
      );
    }

    dbOps().transaction(
      () => {
        if (eventRules) {
          Object.values(eventRules).forEach(eventRule => {
            dbOps().addEventRule(processId, eventRule);
            if (modifiedProcess.eventRuleSequence) {
              modifiedProcess.eventRuleSequence.push(eventRule.__studioId);
            } else {
              modifiedProcess.eventRuleSequence = [eventRule.__studioId];
            }
          });
        }
        if (stateRules) {
          Object.values(stateRules).forEach(stateRule => {
            dbOps().addStateRule(processId, stateRule);
            if (modifiedProcess.stateRuleSequence) {
              modifiedProcess.stateRuleSequence.push(stateRule.__studioId);
            } else {
              modifiedProcess.stateRuleSequence = [stateRule.__studioId];
            }
          });
        }
        dbOps().updateProcess(processId, modifiedProcess);
      },
      { intent: Intents.IMPORT_MULTI_PROCESS_TEMPLATE }
    );
    showToast({
      title: "Process Updated Successfully!",
      description: "Process updated as per the template configuration",
      type: ToastTypes.SUCCESS
    });
  }
};

export const retriggerReferencedAppSettingsCalc = (
  appSettings: AppSettings,
  skipMergingOldReferenceSettings: boolean = true
) => {
  dbOps().transaction(
    () => {
      const { referencedAppSettings, shouldUpdateReferenceSettings } =
        reconcileReferencedAppSettings({
          megaJSON: dbOps().megaJSON,
          appSettings,
          environment: getEnvironment(),
          skipMergingOldReferenceSettings
        });

      if (shouldUpdateReferenceSettings) {
        dbOps().replaceAllAppSettings(referencedAppSettings);
      }
    },
    { skipLoggingInLedgerAndUndoStack: true }
  );
};
